import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';

import { ButtonModule, DialogRef, IconModule, UaParserService, WINDOW } from '@hglobal/common';
import { TranslateModule } from '@ngx-translate/core';

import { BeforeInstallPromptEvent, IS_PWA_INSTALLED } from './pwa-setup.entities';

@Component({
  imports: [TranslateModule, ButtonModule, IconModule, NgOptimizedImage],
  selector: 'pp-pwa-setup',
  templateUrl: './pwa-setup.component.html',
  styleUrls: ['./pwa-setup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PwaSetupComponent implements OnInit {
  readonly #dialogRef = inject(DialogRef);
  readonly #uaParser = inject(UaParserService);
  readonly #window = inject(WINDOW);

  #promptEvent: BeforeInstallPromptEvent | null = null;

  protected readonly isIOS = this.#uaParser.isIOS;
  isInstalling = signal(false);
  canInstallPwa = signal(false);

  ngOnInit(): void {
    this.#initializePwaPrompt();
  }

  closeDialog(): void {
    this.#dialogRef.close();
  }

  installPwa(): void {
    if (!this.canInstallPwa()) return;

    this.isInstalling.set(true);
    this.#updateInstallButton(false);

    if (this.#promptEvent) {
      this.#promptEvent.prompt();
      this.#handleUserChoice();
    }
  }

  #initializePwaPrompt(): void {
    this.#window.addEventListener('beforeinstallprompt', this.#onBeforeInstallPrompt);
  }

  readonly #onBeforeInstallPrompt = (event: Event): void => {
    event.preventDefault();
    this.#promptEvent = event as BeforeInstallPromptEvent;
    this.#updateInstallButton(true);
  };

  async #handleUserChoice(): Promise<void> {
    if (!this.#promptEvent) return;

    const choiceResult = await this.#promptEvent.userChoice;
    if (choiceResult.outcome === 'accepted') {
      localStorage.setItem(IS_PWA_INSTALLED, 'true');
    }
    this.#promptEvent = null;
  }

  #updateInstallButton(isEnabled: boolean): void {
    this.canInstallPwa.set(isEnabled);
  }
}
